import { useGetUserMetrics } from '@/api/users/queries'
import { useModalsStore } from '@/stores/modals'
import { useUserStore } from '@/stores/user'
import {
  CONSULTATIONS_LIMIT,
  RECORDING_TIME_LIMIT,
  CUSTOM_PROMPTS_LIMITS,
  CONSUMPTION_THRESHOLDS,
} from '@/constants/resourceLimits'
import {
  wasPaywallThresholdShown,
  markPaywallThresholdShown,
} from '@/utils/paywallThresholdStorage'
import { resolveCloserToBlockingType } from '@/utils/resourceLimitUtils'

const resolveThresholdModalType = (
  definition,
  consultations,
  recordingSeconds,
  consultationsLimit,
  recordingLimitSeconds,
) => {
  const hitConsultation = consultations >= definition.minConsultations
  const hitRecording = recordingSeconds >= definition.minRecordingSeconds

  if (hitConsultation && hitRecording) {
    return resolveCloserToBlockingType(
      consultations,
      recordingSeconds,
      consultationsLimit,
      recordingLimitSeconds,
    )
  }

  return hitConsultation ? 'consultations' : 'recordingTime'
}

export const useUserMetricsStore = defineStore('userMetrics', () => {
  const modalsStore = useModalsStore()
  const userStore = useUserStore()
  const { limits: userPlanLimits, isProfileCompleted, isProUser } =
    storeToRefs(userStore)
  const {
    data: userMetrics,
    isLoading: isUserMetricsLoading,
    refetch: refetchUserMetrics,
  } = useGetUserMetrics()

  const resourceLimitModalType = ref('consultations')

  const consultationsLimit = computed(
    () => userPlanLimits.value?.consultationsPerMonth ?? CONSULTATIONS_LIMIT,
  )
  const recordingLimitSeconds = computed(
    () =>
      userPlanLimits.value?.recordingSecondsPerMonth ?? RECORDING_TIME_LIMIT,
  )

  const hasConsultations = computed(() => {
    return totalConsultations.value > 0
  })
  const totalConsultations = computed(() => {
    return userMetrics.value?.consultations.totalInLifetime
  })
  const totalConsultationsInPeriod = computed(() => {
    return userMetrics.value?.consultations.totalInPeriod ?? 0
  })

  const totalRecordingTime = computed(() => {
    return userMetrics.value?.recordingTime.totalInLifetime
  })
  const totalRecordingTimeInPeriod = computed(() => {
    return userMetrics.value?.recordingTime.totalInPeriod ?? 0
  })

  const yearMonth = computed(() => {
    return userMetrics.value?.yearMonth || null
  })

  const isFirstAccessThisPeriod = computed(() => {
    return userMetrics.value?.isFirstAccessThisPeriod === true
  })

  const totalEditedDaaiPrompts = computed(() => {
    return userMetrics.value?.customPrompts?.editedDaai?.totalInLifetime ?? 0
  })
  const totalEditedDaaiPromptsInPeriod = computed(() => {
    return userMetrics.value?.customPrompts?.editedDaai?.totalInPeriod ?? 0
  })
  const totalFreeTextPrompts = computed(() => {
    return userMetrics.value?.customPrompts?.freeText?.totalInLifetime ?? 0
  })
  const totalFreeTextPromptsInPeriod = computed(() => {
    return userMetrics.value?.customPrompts?.freeText?.totalInPeriod ?? 0
  })
  const totalAiGeneratedPrompts = computed(() => {
    return userMetrics.value?.customPrompts?.aiGenerated?.totalInLifetime ?? 0
  })
  const totalAiGeneratedPromptsInPeriod = computed(() => {
    return userMetrics.value?.customPrompts?.aiGenerated?.totalInPeriod ?? 0
  })
  const totalCustomPromptsAll = computed(() => {
    const allTotal = userMetrics.value?.customPrompts?.all?.totalInLifetime
    if (typeof allTotal === 'number') return allTotal

    return (
      totalEditedDaaiPrompts.value +
      totalFreeTextPrompts.value +
      totalAiGeneratedPrompts.value
    )
  })

  const totalCustomPrompts = computed(() => ({
    editedDaai: totalEditedDaaiPrompts.value,
    freeText: totalFreeTextPrompts.value,
    aiGenerated: totalAiGeneratedPrompts.value,
  }))

  const totalCustomPromptsInPeriod = computed(() => ({
    editedDaai: totalEditedDaaiPromptsInPeriod.value,
    freeText: totalFreeTextPromptsInPeriod.value,
    aiGenerated: totalAiGeneratedPromptsInPeriod.value,
  }))

  const exceededCustomPromptsLimit = computed(() => {
    return {
      editedDaai:
        totalEditedDaaiPrompts.value >= CUSTOM_PROMPTS_LIMITS.editedDaai,
      freeText: totalFreeTextPrompts.value >= CUSTOM_PROMPTS_LIMITS.freeText,
      aiGenerated:
        totalAiGeneratedPrompts.value >= CUSTOM_PROMPTS_LIMITS.aiGenerated,
    }
  })

  const isAnyCustomPromptCategoryLimitReached = computed(() => {
    return (
      exceededCustomPromptsLimit.value.editedDaai ||
      exceededCustomPromptsLimit.value.freeText ||
      exceededCustomPromptsLimit.value.aiGenerated
    )
  })

  const isAllCustomPromptsLimitReached = computed(() => {
    return (
      exceededCustomPromptsLimit.value.editedDaai &&
      exceededCustomPromptsLimit.value.freeText &&
      exceededCustomPromptsLimit.value.aiGenerated
    )
  })

  const exceededConsultationsLimit = computed(() => {
    return totalConsultationsInPeriod.value >= consultationsLimit.value
  })

  const exceededRecordingTimeLimit = computed(() => {
    return totalRecordingTimeInPeriod.value >= recordingLimitSeconds.value
  })

  const isResourceUseLimitReached = computed(() => {
    return exceededConsultationsLimit.value || exceededRecordingTimeLimit.value
  })

  const openThresholdModal = (thresholdId, type) => {
    if (!yearMonth.value) return false
    if (wasPaywallThresholdShown(yearMonth.value, thresholdId)) return false
    if (!isProfileCompleted.value || isProUser.value) return false

    markPaywallThresholdShown(yearMonth.value, thresholdId)
    resourceLimitModalType.value = type
    modalsStore.showResourceUseLimitModal = true
    return true
  }

  const evaluatePendingThresholdModal = () => {
    if (!yearMonth.value || isFirstAccessThisPeriod.value) return
    if (!isProfileCompleted.value || isProUser.value) return

    const consultations = totalConsultationsInPeriod.value
    const recordingSeconds = totalRecordingTimeInPeriod.value

    for (const definition of CONSUMPTION_THRESHOLDS) {
      const hitConsultation = consultations >= definition.minConsultations
      const hitRecording = recordingSeconds >= definition.minRecordingSeconds
      if (!hitConsultation && !hitRecording) continue

      const modalType = resolveThresholdModalType(
        definition,
        consultations,
        recordingSeconds,
        consultationsLimit.value,
        recordingLimitSeconds.value,
      )

      if (openThresholdModal(definition.id, modalType)) return
    }
  }

  const checkResourceLimits = ({
    consultationsNew,
    consultationsOld,
    recordingTimeNew,
    recordingTimeOld,
  }) => {
    if (consultationsOld === undefined || recordingTimeOld === undefined) return

    for (const definition of CONSUMPTION_THRESHOLDS) {
      const crossedConsultation =
        consultationsNew >= definition.minConsultations &&
        consultationsOld < definition.minConsultations
      const crossedRecording =
        recordingTimeNew >= definition.minRecordingSeconds &&
        recordingTimeOld < definition.minRecordingSeconds

      if (!crossedConsultation && !crossedRecording) continue

      const modalType = resolveThresholdModalType(
        definition,
        consultationsNew,
        recordingTimeNew,
        consultationsLimit.value,
        recordingLimitSeconds.value,
      )

      if (openThresholdModal(definition.id, modalType)) return
    }
  }

  watch(
    [totalConsultationsInPeriod, totalRecordingTimeInPeriod],
    (newValue, oldValue) => {
      checkResourceLimits({
        consultationsNew: newValue[0],
        consultationsOld: oldValue?.[0],
        recordingTimeNew: newValue[1],
        recordingTimeOld: oldValue?.[1],
      })
    },
  )

  watch(
    userMetrics,
    (metrics) => {
      if (!metrics) return
      evaluatePendingThresholdModal()
    },
    { immediate: true },
  )

  watch([isProfileCompleted, isProUser], () => {
    evaluatePendingThresholdModal()
  })

  return {
    userMetrics,
    isUserMetricsLoading,
    resourceLimitModalType,
    totalConsultations,
    totalConsultationsInPeriod,
    totalRecordingTime,
    totalRecordingTimeInPeriod,
    yearMonth,
    isFirstAccessThisPeriod,
    hasConsultations,
    isResourceUseLimitReached,
    exceededConsultationsLimit,
    exceededRecordingTimeLimit,
    exceededCustomPromptsLimit,
    totalCustomPromptsAll,
    isAnyCustomPromptCategoryLimitReached,
    isAllCustomPromptsLimitReached,
    checkResourceLimits,
    evaluatePendingThresholdModal,
    refetchUserMetrics,
  }
})
